import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import ROUTE_NAVIGATION from './routeQuery';
import loadable from '../components/loadable';
import NotFoundPage from './NotFoundPage/NotFoundPage';
import Loader from '../components/Loader';

const PageRenderer = loadable(() => import('../components/PageRenderer/PageRenderer'));
const Product = loadable(() => import('../screens/Product/ProductRenderer'));
const Category = loadable(() => import('../screens/Category/Category'));

const sanitizePath = (path) => {
    if (path.startsWith('//')) path = path.substring(2);
    if (path.startsWith('/')) path = path.substring(1);
    if (path.endsWith('/')) path = path.slice(0, -1);
    if (path === '') path = '/';
    return path;
};

// Pamięć podręczna ostatniego poprawnego stanu i ścieżki
let lastValidData = null;
let lastValidPath = '/';

const DynamicRouteResolver = ({ components, loaderComponent: LoaderComponent = Loader, errorComponent, ...props }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { pathname } = location;
    const path = sanitizePath(pathname);
    const [retryCount, setRetryCount] = useState(0);
    const [showNotFound, setShowNotFound] = useState(false);

    // Maksymalna liczba prób przed pokazaniem 404
    const MAX_RETRIES = 2;
    // Skonfiguruj zapytanie GraphQL
    const { loading, error, data, refetch } = useQuery(ROUTE_NAVIGATION, {
        variables: { url: path },
        fetchPolicy: "cache-and-network", // Użyj cache-and-network aby pokazać dane z cache i odświeżyć
        ssr: true,
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all' // Obsługuj błędy bez przerywania
    });
    // Efekt, który zapisuje ostatni poprawny stan
    useEffect(() => {
        if (!loading && !error && data && data.route) {
            lastValidData = data;
            lastValidPath = path;
        }
    }, [loading, error, data, path]);

    // Efekt obsługi błędów
    useEffect(() => {
        if (error && retryCount < MAX_RETRIES) {
            // Automatycznie ponów zapytanie po błędzie
            const retryTimeout = setTimeout(() => {
                refetch();
                setRetryCount(prev => prev + 1);
            }, 1000); // Ponów po 1 sekundzie
            
            return () => clearTimeout(retryTimeout);
        } else if ((error || (!data || !data.route)) && retryCount >= MAX_RETRIES) {
            // Po wyczerpaniu prób pokaż 404
            const notFoundTimeout = setTimeout(() => {
                setShowNotFound(true);
            }, 500);
            
            return () => clearTimeout(notFoundTimeout);
        }
    }, [error, retryCount, refetch, data]);

    // Fallback dla SSR bez wcześniejszych danych
    if (typeof window === 'undefined' && !data && !lastValidData) {
        return <div id="ssr-loader-wrapper"><LoaderComponent /></div>;
    }

    // Wskaźnik ładowania jest obsługiwany przez SmoothPageTransition
    if (loading && typeof window !== 'undefined' && !window.document.querySelector('.smooth-page-transition-container')) {
        // Tylko poza kontekstem SmoothPageTransition pokaż loader
        return <div id="loader-wrapper"><LoaderComponent /></div>;
    }

    // Jeśli to błąd, ale mamy ostatnie poprawne dane i jesteśmy w trakcie ponownych prób
    if ((error || (!data || !data.route)) && lastValidData && retryCount < MAX_RETRIES) {
        // Podczas ponownych prób pokaż ostatnią poprawną zawartość zamiast błędu
        return renderContent(lastValidData);
    }

    if (data && data.route==null) {
        return <div><NotFoundPage /></div>;
    }

    // Jeśli wszystkie próby zawiodły i showNotFound jest true
    if (showNotFound) {
        return <div><NotFoundPage /></div>;
    }

    // Normalne renderowanie z danymi
    if (data && data.route) {
        return renderContent(data);
    }

    // Domyślny fallback podczas ładowania
    return <div id="default-loader"><LoaderComponent /></div>;

    // Funkcja renderująca zawartość w zależności od typu
    function renderContent(currentData) {
        if (!currentData || !currentData.route) return null;
        
        switch (currentData.route.__typename) {
            case 'CmsPage': 
                return <PageRenderer routeData={currentData.route} />;
            case 'SimpleProduct':
            case 'GroupedProduct': 
                return <Product routeData={currentData.route} />;
            case 'CategoryTree':
                if (!currentData.route.uid) return null;
                return <div><Category uid={currentData.route.uid} pageData={currentData.route} /></div>;
            default: 
                // Tylko po wyczerpaniu wszystkich prób pokazujemy NotFound
                if (showNotFound) {
                    return <div><NotFoundPage /></div>;
                }
                return <div id="fallback-loader"><LoaderComponent /></div>;
        }
    }
};

DynamicRouteResolver.propTypes = {
    components: PropTypes.shape({}),
    loaderComponent: PropTypes.elementType,
    errorComponent: PropTypes.elementType,
};

export default DynamicRouteResolver;